import React from 'react'
import { supabase } from '../auth/supabaseClient'

// Lazy-load ticket helpers to keep chunks tidy and avoid mixed dynamic/static imports
const Tickets = {
  createTicket: async (type: 'bug'|'question'|'idea'|'other', subject: string, message: string) => (await import('../lib/tickets')).createTicket(type, subject, message),
  listMyTickets: async (limit: number) => (await import('../lib/tickets')).listMyTickets(limit),
  listTicketComments: async (ticketId: string) => (await import('../lib/tickets')).listTicketComments(ticketId),
  addTicketComment: async (ticketId: string, msg: string) => (await import('../lib/tickets')).addTicketComment(ticketId, msg),
  updateTicketStatus: async (ticketId: string, status: 'open'|'closed') => (await import('../lib/tickets')).updateTicketStatus(ticketId, status),
  listTicketAttachments: async (ticketId: string) => (await import('../lib/tickets')).listTicketAttachments(ticketId),
  addTicketAttachment: async (ticketId: string, objectName: string, contentType?: string | null) => (await import('../lib/tickets')).addTicketAttachment(ticketId, objectName, contentType),
}

export function HelpForm({ onClose }: { onClose: () => void }) {
  const [type, setType] = React.useState<'bug'|'question'|'idea'|'other'>('bug')
  const [subject, setSubject] = React.useState('')
  const [message, setMessage] = React.useState('')
  const [includeDiag, setIncludeDiag] = React.useState<boolean>(true)
  const [busy, setBusy] = React.useState(false)
  const [done, setDone] = React.useState<string | null>(null)
  const [tickets, setTickets] = React.useState<any[]>([])
  const [active, setActive] = React.useState<any|null>(null)
  const [comments, setComments] = React.useState<any[]>([])
  const [reply, setReply] = React.useState('')
  const [lastSentAt, setLastSentAt] = React.useState<number>(0)
  const [offline, setOffline] = React.useState<boolean>(typeof navigator !== 'undefined' ? !navigator.onLine : false)
  const [attachFile, setAttachFile] = React.useState<File | null>(null)
  const [attachments, setAttachments] = React.useState<any[]>([])
  const [userFilterStatus, setUserFilterStatus] = React.useState<'all'|'open'|'closed'>('all')
  const [attachError, setAttachError] = React.useState<string>('')

  async function compressImageToUnder(blob: Blob, maxBytes = 1_000_000): Promise<File | null> {
    try {
      const bitmap = await createImageBitmap(blob as any)
      const maxDim = 1600
      const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height))
      const w = Math.max(1, Math.round(bitmap.width * scale))
      const h = Math.max(1, Math.round(bitmap.height * scale))
      const canvas = document.createElement('canvas')
      canvas.width = w; canvas.height = h
      const ctx = canvas.getContext('2d')!
      ctx.drawImage(bitmap, 0, 0, w, h)
      let quality = 0.8
      for (let i=0; i<5; i++) {
        const out: Blob = await new Promise((res, rej) => canvas.toBlob(b => b?res(b):rej(new Error('toBlob failed')), 'image/jpeg', quality)) as Blob
        if (out.size <= maxBytes) {
          return new File([out], 'attachment.jpg', { type: 'image/jpeg' })
        }
        quality -= 0.2
        if (quality < 0.2) break
      }
      return null
    } catch { return null }
  }

  // Load my tickets initially
  React.useEffect(() => { (async()=>{ try { setTickets(await Tickets.listMyTickets(50)) } catch { /* ignore */ } })() }, [])
  // Load comments and attachments for active ticket; also auto-refresh on broadcast
  React.useEffect(() => { (async()=>{ if (active) try { setComments(await Tickets.listTicketComments(active.id)); setAttachments(await Tickets.listTicketAttachments(active.id)) } catch { setComments([]); setAttachments([]) } })() }, [active])
  React.useEffect(() => {
    const onUpd = async () => { if (active) { try { setComments(await Tickets.listTicketComments(active.id)) } catch { /* ignore */ } } }
    window.addEventListener('pt_ticket_update' as any, onUpd)
    return () => window.removeEventListener('pt_ticket_update' as any, onUpd)
  }, [active])
  // Periodic auto-refresh while viewing a thread
  React.useEffect(() => {
    if (!active) return
    let mounted = true
    const id = setInterval(async () => {
      try {
        if (!mounted) return
        setComments(await Tickets.listTicketComments(active.id))
        setAttachments(await Tickets.listTicketAttachments(active.id))
      } catch { /* ignore */ }
    }, 25000)
    return () => { mounted = false; clearInterval(id) }
  }, [active])
  // offline banner
  React.useEffect(() => {
    const on = () => setOffline(false)
    const off = () => setOffline(true)
    window.addEventListener('online', on)
    window.addEventListener('offline', off)
    // Drain queues when back online
    const drain = async () => {
      // Send queued tickets
      try {
        const raw = localStorage.getItem('pt_ticket_queue')
        const q: Array<{ type:'bug'|'question'|'idea'|'other'; subject:string; message:string; includeDiag?:boolean }> = raw ? JSON.parse(raw) : []
        const remaining: typeof q = []
        for (const t of q) {
          try {
            const body = (t.includeDiag ? (t.message + diagnostics()) : t.message)
            const id = await Tickets.createTicket(t.type, t.subject, body)
            if (!id) remaining.push(t)
          } catch { remaining.push(t) }
        }
        localStorage.setItem('pt_ticket_queue', JSON.stringify(remaining))
        // refresh list if something was sent
        if (q.length !== remaining.length) { try { setTickets(await Tickets.listMyTickets(50)) } catch { /* ignore */ } }
      } catch { /* ignore */ }
      // Send queued replies
      try {
        const rawR = localStorage.getItem('pt_ticket_reply_queue')
        const rq: Array<{ ticketId: string; message: string }> = rawR ? JSON.parse(rawR) : []
        const remainingR: typeof rq = []
        for (const r of rq) {
          try {
            const id = await Tickets.addTicketComment(r.ticketId, r.message)
            if (!id) remainingR.push(r)
          } catch { remainingR.push(r) }
        }
        localStorage.setItem('pt_ticket_reply_queue', JSON.stringify(remainingR))
        if (active && rq.length !== remainingR.length) { try { setComments(await Tickets.listTicketComments(active.id)) } catch { /* ignore */ } }
      } catch { /* ignore */ }
    }
    window.addEventListener('online', drain)
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); window.removeEventListener('online', drain) }
  }, [active])

  const relTime = (ts: string) => {
    const d = new Date(ts).getTime()
    const diff = Math.max(0, Date.now() - d)
    const mins = Math.floor(diff / 60000)
    if (mins < 1) return 'just now'
    if (mins < 60) return `${mins}m ago`
    const hrs = Math.floor(mins / 60)
    if (hrs < 24) return `${hrs}h ago`
    const days = Math.floor(hrs / 24)
    return `${days}d ago`
  }

  function applyTemplate(t: 'bug'|'question'|'idea') {
    setType(t)
    if (t === 'bug') {
      setSubject(s => s.trim() ? s : 'Bug: what went wrong?')
      setMessage(m => m.trim() ? m : `What happened?
Steps to reproduce:
1.
2.
3.
Expected:
Actual:

Device/browser: (auto)
`)
    } else if (t === 'question') {
      setSubject(s => s.trim() ? s : 'Question about a feature')
      setMessage(m => m.trim() ? m : 'My question:')
    } else if (t === 'idea') {
      setSubject(s => s.trim() ? s : 'Idea: ')
      setMessage(m => m.trim() ? m : 'Describe your idea:')
    }
  }

  function diagnostics(): string {
    try {
      const ua = navigator.userAgent
      const appVer = (import.meta as any).env?.VITE_APP_VERSION || (document.querySelector('meta[name="version"]') as any)?.content || 'dev'
      const pwa = (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) || (navigator as any).standalone === true
      const sw = 'serviceWorker' in navigator
      const camPerm = (navigator as any).permissions ? 'queryable' : 'n/a'
      const diag = { appVer, ua, pwa, sw, camPerm, tz: Intl.DateTimeFormat().resolvedOptions().timeZone }
      return `\n\n---\nDiagnostics: ${JSON.stringify(diag)}`
    } catch { return '' }
  }

  const canSend = !busy && subject.trim().length >= 6 && message.trim().length >= 10 && (Date.now() - lastSentAt > 30000)
  const cooldownLeft = Math.max(0, 30 - Math.floor((Date.now() - lastSentAt)/1000))

  // Persist draft for new ticket
  React.useEffect(() => {
    try { localStorage.setItem('pt_ticket_draft', JSON.stringify({ type, subject, message, includeDiag })) } catch { /* ignore */ }
  }, [type, subject, message, includeDiag])
  React.useEffect(() => {
    try {
      const raw = localStorage.getItem('pt_ticket_draft')
      if (raw) {
        const d = JSON.parse(raw)
        if (d.type) setType(d.type)
        if (typeof d.subject === 'string') setSubject(d.subject)
        if (typeof d.message === 'string') setMessage(d.message)
        if (typeof d.includeDiag === 'boolean') setIncludeDiag(d.includeDiag)
      }
    } catch { /* ignore */ }
  }, [])
  // Persist draft for replies per active ticket
  React.useEffect(() => {
    if (!active) return
    try { const raw = localStorage.getItem(`pt_ticket_reply_${active.id}`); if (raw) setReply(raw) } catch { /* ignore */ }
  }, [active])
  React.useEffect(() => {
    if (!active) return
    try { localStorage.setItem(`pt_ticket_reply_${active.id}`, reply) } catch { /* ignore */ }
  }, [active, reply])

  return (
    <div className="container">
      <div className="hero">
        {offline && (
          <div className="list-item" style={{background:'rgba(255,200,0,0.1)', border:'1px solid rgba(255,200,0,0.25)'}}>Offline — you can draft a ticket and send when back online.</div>
        )}
        <div className="list-item" style={{display:'grid', gridTemplateColumns:'1fr', gap:8}}>
          <strong>Your tickets</strong>
          <div className="list" style={{maxHeight:220, overflow:'auto'}}>
            <div className="list-item wrap" style={{gap:8}}>
              <div style={{display:'flex', gap:6, overflowX:'auto'}}>
                {(['all','open','closed'] as const).map(s => (
                  <button key={s} className={`btn btn-ghost${userFilterStatus===s?' tab active':''}`} onClick={()=>setUserFilterStatus(s)} style={{minWidth:0, padding:'8px 10px'}}>
                    {s[0].toUpperCase()+s.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            {tickets.filter(t => userFilterStatus==='all' ? true : t.status===userFilterStatus).map(t => (
              <div key={t.id} className="list-item" style={{cursor:'pointer'}} onClick={()=>setActive(t)}>
                <div style={{display:'grid'}}>
                  <div style={{fontWeight:700}}>{t.subject}</div>
                  <div style={{fontSize:12, opacity:0.8}}>{t.type} • {relTime(t.created_at)} • <span className="hud-chip" style={{textTransform:'capitalize'}}>{t.status}</span></div>
                </div>
              </div>
            ))}
            {tickets.length===0 && <div className="list-item" style={{opacity:0.8}}>No tickets yet</div>}
          </div>
        </div>
        {active && (
          <div className="list-item" style={{display:'grid', gap:8}}>
            <div style={{fontWeight:800}}>{active.subject}</div>
            <div style={{fontSize:12, opacity:0.85}}>{active.type} • {relTime(active.created_at)} • <span className="hud-chip" style={{textTransform:'capitalize'}}>{active.status}</span></div>
            <div className="list" style={{maxHeight:240, overflow:'auto'}}>
              {comments.map(c => {
                const author = c.user_id === active.user_id ? 'You' : 'Support'
                return (
                  <div key={c.id} className="list-item" style={{display:'grid', gap:4}}>
                    <div style={{fontSize:12, opacity:0.75}}>{author} • {relTime(c.created_at)}</div>
                    <div style={{fontSize:14, whiteSpace:'pre-wrap'}}>{c.message}</div>
                  </div>
                )
              })}
              {comments.length===0 && <div className="list-item" style={{opacity:0.8}}>No conversation yet</div>}
            </div>
            {attachments.length > 0 && (
              <div className="list-item" style={{display:'grid', gap:6}}>
                <div style={{opacity:.8}}>Attachments</div>
                {attachments.map((a:any) => (
                  <div key={a.id} style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                    <div style={{overflow:'hidden', textOverflow:'ellipsis'}}>{a.object_name}</div>
                    <button className="btn btn-ghost" onClick={async()=>{
                      try {
                        const { data } = await supabase.storage.from('ticket-attachments').createSignedUrl(a.object_name, 60)
                        if (data?.signedUrl) { window.open(data.signedUrl, '_blank') }
                      } catch { /* ignore */ }
                    }}>Open</button>
                  </div>
                ))}
              </div>
            )}
            <div style={{display:'flex', gap:8, justifyContent:'space-between', alignItems:'center'}}>
              <div/>
              {active.status === 'open' ? (
                <button className="btn btn-ghost" onClick={async()=>{
                  try {
                    const ok = await Tickets.updateTicketStatus(active.id, 'closed')
                    if (ok) setActive((a:any)=> a ? {...a, status:'closed'} : a)
                  } catch { /* ignore */ }
                }}>Close ticket</button>
              ) : (
                <button className="btn btn-ghost" onClick={async()=>{
                  try {
                    const ok = await Tickets.updateTicketStatus(active.id, 'open')
                    if (ok) setActive((a:any)=> a ? {...a, status:'open'} : a)
                  } catch { /* ignore */ }
                }}>Reopen ticket</button>
              )}
            </div>
            <div className="stack-grid" style={{display:'grid', gridTemplateColumns:'1fr auto', gap:8}}>
              <input className="input" placeholder="Reply…" value={reply} onChange={e=>setReply(e.target.value)} />
              <button className="btn btn-primary" disabled={!reply.trim()} onClick={async()=>{
                if (offline) {
                  try {
                    const raw = localStorage.getItem('pt_ticket_reply_queue')
                    const q: Array<{ ticketId: string; message: string }> = raw ? JSON.parse(raw) : []
                    q.push({ ticketId: active.id, message: reply.trim() })
                    localStorage.setItem('pt_ticket_reply_queue', JSON.stringify(q))
                    setReply('')
                    alert('Reply queued — will send when online')
                  } catch { /* ignore */ }
                  return
                }
                const id = await Tickets.addTicketComment(active.id, reply.trim())
                if (id) { setReply(''); setComments(await Tickets.listTicketComments(active.id)) }
              }}>Send</button>
            </div>
          </div>
        )}
        {done ? (
          <div className="list-item" style={{justifyContent:'space-between'}}>
            <div>Thanks! Ticket <strong>{done.slice(0,8)}…</strong> received.</div>
            <button className="btn btn-primary" onClick={onClose}>Close</button>
          </div>
        ) : (
          <>
            <div className="list-item stack-grid" style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:8}}>
              <select className="input" value={type} onChange={e=>setType(e.target.value as any)}>
                <option value="bug">Bug</option>
                <option value="question">Question</option>
                <option value="idea">Idea</option>
                <option value="other">Other</option>
              </select>
              <input className="input" placeholder="Subject" value={subject} onChange={e=>setSubject(e.target.value)} />
            </div>
            <div className="list-item" style={{display:'flex', flexWrap:'wrap', gap:8}}>
              <div style={{opacity:.8}}>Templates:</div>
              <button className="btn btn-ghost" onClick={()=>applyTemplate('bug')}>Bug</button>
              <button className="btn btn-ghost" onClick={()=>applyTemplate('question')}>Question</button>
              <button className="btn btn-ghost" onClick={()=>applyTemplate('idea')}>Idea</button>
              <span style={{flex:'1 1 auto'}} />
              <a className="btn btn-ghost" href="https://support.google.com/chrome/answer/2693767" target="_blank" rel="noreferrer">Camera permissions</a>
              <a className="btn btn-ghost" href="https://developer.chrome.com/docs/capabilities/web-apis/mediadevices" target="_blank" rel="noreferrer">Media devices</a>
            </div>
            <div className="list-item">
              <textarea className="input" placeholder="Describe your issue or idea" value={message} onChange={e=>setMessage(e.target.value)} style={{minHeight:120, width:'100%'}} />
            </div>
            <div className="list-item stack-grid" style={{display:'grid', gridTemplateColumns:'1fr auto', gap:8, alignItems:'center'}}>
              <div>
                <input type="file" accept="image/*" onChange={async e=> {
                  setAttachError('')
                  const f = e.currentTarget.files?.[0] || null
                  if (!f) { setAttachFile(null); return }
                  if (!f.type.startsWith('image/')) { setAttachError('Only images allowed'); setAttachFile(null); return }
                  if (f.size <= 1_000_000) { setAttachFile(f); return }
                  const compressed = await compressImageToUnder(f, 1_000_000)
                  if (compressed) setAttachFile(compressed)
                  else { setAttachError('Attachment too large (max 1MB). Pick a smaller image.'); setAttachFile(null) }
                }} />
                <div style={{fontSize:12, opacity:.75, marginTop:4}}>Max 1MB. Large images will be compressed automatically.</div>
                {attachError && <div style={{fontSize:12, color:'#ff6b6b', marginTop:4}}>{attachError}</div>}
              </div>
              {attachFile && <div style={{fontSize:12, opacity:.8, textAlign:'right'}}>{attachFile.name} • {(attachFile.size/1024).toFixed(0)} KB</div>}
            </div>
            <div className="list-item" style={{display:'flex', gap:10, alignItems:'center', justifyContent:'space-between'}}>
              <label className="hud-chip" style={{display:'inline-flex', alignItems:'center', gap:6, cursor:'pointer'}} title="Include basic diagnostics to help support triage.">
                <input type="checkbox" checked={includeDiag} onChange={e=>setIncludeDiag(e.target.checked)} /> Include diagnostics
              </label>
              <div style={{display:'flex', gap:8, alignItems:'center'}}>
                {!canSend && (
                  <span style={{fontSize:12, opacity:.7}}>
                    {subject.trim().length < 6 ? 'Subject too short' : message.trim().length < 10 ? 'Message too short' : cooldownLeft>0 ? `Wait ${cooldownLeft}s` : ''}
                  </span>
                )}
                <button className="btn btn-primary" disabled={!canSend} onClick={async()=>{
                  if (offline) {
                    try {
                      const raw = localStorage.getItem('pt_ticket_queue')
                      const q: Array<any> = raw ? JSON.parse(raw) : []
                      q.push({ type, subject: subject.trim(), message: message.trim(), includeDiag })
                      localStorage.setItem('pt_ticket_queue', JSON.stringify(q))
                      alert('Ticket queued — will send when online')
                    } catch { /* ignore */ }
                    return
                  }
                  setBusy(true)
                  try {
                    const body = includeDiag ? (message.trim() + diagnostics()) : message.trim()
                    const id = await Tickets.createTicket(type, subject.trim(), body)
                    if (id) {
                      setDone(id); setLastSentAt(Date.now())
                      if (attachFile) {
                        const objectName = `${id}/${Date.now()}_${attachFile.name}`
                        try {
                          const { error } = await supabase.storage.from('ticket-attachments').upload(objectName, attachFile, { upsert: true, contentType: attachFile.type })
                          if (!error) {
                            try { await Tickets.addTicketAttachment(id, objectName, attachFile.type) } catch { /* ignore */ }
                          }
                        } catch { /* ignore */ }
                        setAttachFile(null)
                      }
                    }
                    else alert('Could not send—try again later')
                  } finally { setBusy(false) }
                }}>{busy?'Sending…':'Send ticket'}</button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
